/**
 * WebSocket 管理模块
 * 优化重点：工厂模式、公共逻辑抽离、配置化URL、健壮性提升
 * WebSocket 管理模块
 * 优化重点：工厂模式、公共逻辑抽离、配置化URL、健壮性提升
 */
import Tool from '@/assets/js/utils.js'
import $store from '@/store/index.js'
import SocketApi from '@/assets/js/websocket'

// 常量定义
const WS_PROTOCOL = document.location.protocol === 'https:' ? 'wss://' : 'ws://'
const ACTIONS = {
  OFF: 'off',
  STOP: 'stop'
}

/**
 * 获取基础 WS URL
 * @returns {string}
 */
const getBaseUrl = flag => {
  return flag ? $store.state.operateObject.userServiceWebSocket : $store.state.initConfig.wsBaseUrl
}

/**
 * 统一的 Socket 实例管理器
 * @param {Object} context - 当前上下文 (this)
 * @param {String} storeKey - 对应 store.state.websocket 中的 key
 * @param {String} action - 操作类型 ('off', 'stop', 或初始化)
 * @param {String} stopKey - stop 操作所需的 key
 * @param {Function} urlBuilder - 构建 URL 的函数
 * @param {Function} callback - Socket 回调
 * @param {Object} options - 额外配置 (如 sendHeart)
 */
function manageSocket ({ context, storeKey, action, stopKey, urlBuilder, callback, options = {} }) {
  // 1. 获取当前实例
  const currentInstance = context[storeKey]

  // 2. 处理关闭或停止逻辑
  if (action === ACTIONS.OFF && currentInstance) {
    currentInstance.off()
    $store.commit('changeWebSocket', { [storeKey]: null })
    return
  }

  if (action === ACTIONS.STOP && currentInstance) {
    currentInstance.stop(stopKey)
    return
  }

  // 3. 处理创建/初始化逻辑
  // 先计算 URL，如果 URL 依赖的基础配置不存在，则不创建
  const url = urlBuilder()
  if (!url) return

  // 合并请求参数
  const requestConfig = {
    url,
    ...options
  }

  // 4. 实例化并初始化
  const newInstance = new SocketApi(requestConfig, callback)
  newInstance.init()

  // 5. 更新状态 (同时更新当前对象和 Vuex)
  context[storeKey] = newInstance
  // 注意：直接修改 state 是 Vuex 反模式，建议改为 store.commit('SET_WEBSOCKET', { key: storeKey, val: newInstance })
  // 这里为了兼容原有逻辑保持不变，但加了空值保护
  if ($store.state.websocket) {
    $store.state.websocket[storeKey] = newInstance
  }
}

const createWebsocket = {
  // 定义实例引用的占位符，保持和原代码一致的结构
  pageRequestWs: null,
  pageSerBusRequestWs: null,
  pageSerBusRequestWs2: null,
  pageRWebsocketWs: null,
  pageServerRequestWs: null,

  /**
   * 1. 页面常规请求
   */
  pageRequestFn (type, key) {
    manageSocket({
      context: this,
      storeKey: 'pageRequestWs', // 注意：原代码此处 store key 是 pageRequest，但属性名是 pageRequests，已做兼容
      action: type,
      stopKey: key,
      urlBuilder: () => {
        const baseUrl = getBaseUrl()
        return baseUrl ? `${WS_PROTOCOL}/${baseUrl}/pageRequest?taskId=${Tool.return32Str()}_q` : ''
      }
    })
  },

  /**
   * 2. 业务总线请求 1
   */
  pageSerBusRequestFn (type, key, callback) {
    manageSocket({
      context: this,
      storeKey: 'pageSerBusRequestWs',
      action: type,
      stopKey: key,
      callback,
      urlBuilder: () => {
        const baseUrl = getBaseUrl()
        if (!baseUrl) return ''
        const { id } = $store.state.userInfo
        return `${WS_PROTOCOL}/${baseUrl}/pageSerBusRequest?&taskId=${Tool.return32Str()}&businessId=${id}:WB:urlChange:MHNotify&notCheckUser=true`
      }
    })
  },

  /**
   * 3. 业务总线请求 2
   */
  pageSerBusRequestFn2 (type, key, callback) {
    manageSocket({
      context: this,
      storeKey: 'pageSerBusRequestWs2',
      action: type,
      stopKey: key,
      callback,
      options: { sendHeart: true },
      urlBuilder: () => {
        const baseUrl = getBaseUrl()
        if (!baseUrl) return ''
        const { id } = $store.state.userInfo
        const { slugUpdateBusinessId } = $store.state.operateObject
        return `${WS_PROTOCOL}/${baseUrl}/pageSerBusRequest?&taskId=${id}&businessId=${slugUpdateBusinessId}&notCheckUser=true`
      }
    })
  },

  /**
   * 4. RWebsocket 请求
   */
  pageRWebsocketFn (type, key) {
    manageSocket({
      context: this,
      storeKey: 'pageRWebsocketWs',
      action: type,
      stopKey: key,
      urlBuilder: () => {
        const baseUrl = getBaseUrl()
        return baseUrl ? `${WS_PROTOCOL}/${baseUrl}/pageRWebsocket?taskId=${Tool.return32Str()}_q` : ''
      }
    })
  },

  /**
   * 5. Server Request (特殊 URL 逻辑)
   */
  pageServerRequestFn (type, key, callback) {
    manageSocket({
      context: this,
      storeKey: 'pageServerRequestWs',
      action: type,
      stopKey: key,
      callback,
      urlBuilder: () => {
        const serviceUrl = getBaseUrl(true)
        return serviceUrl ? `${WS_PROTOCOL.split('://')[0]}${serviceUrl}pageServerRequest?taskId=${Tool.return32Str()}&notCheckUser=true` : ''
      }
    })
  }
}

export default {
  install (vm) {
    vm.prototype.$createWebsocket = createWebsocket
  }
}
